// p4codec.ts — clean-room TurboPFor p4nzdec128v16 dense-block decoder (TS port).
//
// ATTRIBUTION / NOTICE:
//   The block framing and bit-unpacking logic in this file is ADAPTED from
//   "goturbopfor" by Michael Stapelberg, Copyright 2018 Google Inc.,
//   licensed under the Apache License, Version 2.0.
//   Source: github.com/stapelberg/goturbopfor (file goturbopfor.go).
//   goturbopfor implements the 32-bit / 256-block variant (P4ndec256v32).
//   This file adapts it to Open-Meteo 16-bit / 128-block variant
//   (p4nzdec128v16): 8 lanes, uint16 LE refills, 128 values per dense block,
//   16-bit varbyte exceptions. No GPL TurboPFor (powturbo) source was consulted.
//
//   VERIFIED BIT-EXACT (SESSION 4): this codec is correct as-is. The bitmap
//   exception VALUES use SCALAR bitunpack at width bx (exactly as goturbopfor
//   uses scalar bitunpack32) and the varbyte path uses vbdec16 — both right.
//   The earlier apparent "exception drift" was a DELTA bug, not a codec bug:
//   the caller must undo a 2-D second-difference (delta2d), not a 1-D linear
//   delta. See DECODER-NOTES.md "SESSION 4".
//
// Licensed under the Apache License, Version 2.0.
//
// TS PORT NOTE: this is a faithful translation of poc/p4codec.mjs. The only
// changes are TypeScript type annotations and that `input` is a Uint8Array
// (browser fetch / ArrayBuffer view) rather than a Node Buffer. The numeric
// logic, scratch reuse, and bit operations are byte-for-byte identical.

export function bitunpackScalar(
  input: Uint8Array,
  inOff: number,
  output: Int32Array | Uint16Array,
  outOff: number,
  n: number,
  nbits: number,
): number {
  if (nbits === 0) {
    for (let i = 0; i < n; i++) output[outOff + i] = 0
    return 0
  }
  let p = inOff
  let rbits = 0
  let acc = 0
  const mask = nbits >= 32 ? 0xffffffff : (1 << nbits) - 1
  let op = 0
  while (op < n) {
    if (rbits < nbits) {
      acc = (acc | (input[p] << rbits)) >>> 0
      p++
      rbits += 8
    }
    if (rbits >= nbits) {
      output[outOff + op] = (acc & mask) >>> 0
      op++
      acc = acc >>> nbits
      rbits -= nbits
    }
  }
  return p - inOff
}

// 8 reusable uint32 lane accumulators (module-level scratch; single-threaded).
const _acc = new Uint32Array(8)

export function bitunpack128v16(
  input: Uint8Array,
  inOff: number,
  output: Int32Array | Uint16Array,
  outOff: number,
  n: number,
  nbits: number,
): number {
  if (nbits === 0) {
    for (let i = 0; i < n; i++) output[outOff + i] = 0
    return 0
  }
  let p = inOff
  let bits = 0
  const acc = _acc
  acc[0] = 0
  acc[1] = 0
  acc[2] = 0
  acc[3] = 0
  acc[4] = 0
  acc[5] = 0
  acc[6] = 0
  acc[7] = 0
  const mask = (1 << nbits) - 1
  let op = 0
  // INVARIANT: nbits<=16 and on entry to a refill bits<nbits<=16, so after
  // adding a 16-bit word each lane holds <32 bits — fits in uint32 with shifts.
  while (op < n) {
    if (bits < nbits) {
      // refill: each of 8 lanes gets one 16-bit LE word at bit position `bits`
      acc[0] = (acc[0] | ((input[p] | (input[p + 1] << 8)) << bits)) >>> 0
      acc[1] = (acc[1] | ((input[p + 2] | (input[p + 3] << 8)) << bits)) >>> 0
      acc[2] = (acc[2] | ((input[p + 4] | (input[p + 5] << 8)) << bits)) >>> 0
      acc[3] = (acc[3] | ((input[p + 6] | (input[p + 7] << 8)) << bits)) >>> 0
      acc[4] = (acc[4] | ((input[p + 8] | (input[p + 9] << 8)) << bits)) >>> 0
      acc[5] = (acc[5] | ((input[p + 10] | (input[p + 11] << 8)) << bits)) >>> 0
      acc[6] = (acc[6] | ((input[p + 12] | (input[p + 13] << 8)) << bits)) >>> 0
      acc[7] = (acc[7] | ((input[p + 14] | (input[p + 15] << 8)) << bits)) >>> 0
      p += 16
      bits += 16
    }
    // bits>=nbits guaranteed here (nbits<=16<=bits after a refill, or carried)
    output[outOff + op] = acc[0] & mask
    acc[0] = acc[0] >>> nbits
    output[outOff + op + 1] = acc[1] & mask
    acc[1] = acc[1] >>> nbits
    output[outOff + op + 2] = acc[2] & mask
    acc[2] = acc[2] >>> nbits
    output[outOff + op + 3] = acc[3] & mask
    acc[3] = acc[3] >>> nbits
    output[outOff + op + 4] = acc[4] & mask
    acc[4] = acc[4] >>> nbits
    output[outOff + op + 5] = acc[5] & mask
    acc[5] = acc[5] >>> nbits
    output[outOff + op + 6] = acc[6] & mask
    acc[6] = acc[6] >>> nbits
    output[outOff + op + 7] = acc[7] & mask
    acc[7] = acc[7] >>> nbits
    op += 8
    bits -= nbits
  }
  return p - inOff
}

export function vbdec16(
  input: Uint8Array,
  inOff: number,
  output: Int32Array,
  outOff: number,
  n: number,
): number {
  let p = inOff
  if (n === 0) return 0
  if (input[p] === 0xff) {
    p++
    for (let i = 0; i < n; i++) {
      output[outOff + i] = input[p] | (input[p + 1] << 8)
      p += 2
    }
    return p - inOff
  }
  for (let i = 0; i < n; i++) {
    let x = input[p]
    p++
    if (x < 177) {
      /* single byte */
    } else if (x < 241) {
      x = input[p] + ((x - 177) << 8) + 177
      p++
    } else if (x < 249) {
      x = input[p] + (input[p + 1] << 8) + ((x - 241) << 16) + 16561
      p += 2
    } else {
      const _b = x - 249
      let v = 0
      const total = 3 + _b
      for (let k = 0; k < total; k++) v += input[p + k] * Math.pow(2, 8 * k)
      const bm = 24 + 8 * _b
      x = bm >= 32 ? v >>> 0 : v % Math.pow(2, bm)
      p += total
    }
    output[outOff + i] = x & 0xffff
  }
  return p - inOff
}

// Reusable exception scratch (grown on demand). n<=128 so nex<=128.
const _exc = new Int32Array(128)

// Per-byte popcount table (for fast bitmap-exception popcount).
const _POPC = new Uint8Array(256)
for (let i = 0; i < 256; i++)
  _POPC[i] =
    (i & 1) +
    ((i >> 1) & 1) +
    ((i >> 2) & 1) +
    ((i >> 3) & 1) +
    ((i >> 4) & 1) +
    ((i >> 5) & 1) +
    ((i >> 6) & 1) +
    ((i >> 7) & 1)

export function p4dec128v16(
  input: Uint8Array,
  inOff: number,
  output: Int32Array | Uint16Array,
  outOff: number,
  n: number,
  dense: boolean,
): number {
  if (n === 0) return 0
  let p = inOff
  const hdr = input[p]
  p++
  const t0 = (hdr & 0x80) >> 7
  const t1 = (hdr & 0x40) >> 6
  const b = hdr & 0x3f

  if (t0 === 1 && t1 === 1) {
    // constant block: b bits broadcast
    let u = 0
    const nbytes = (b + 7) >> 3
    for (let k = 0; k < nbytes; k++) u += input[p + k] * Math.pow(2, 8 * k)
    if (b < 32) u = u % Math.pow(2, b)
    for (let i = 0; i < n; i++) output[outOff + i] = u
    return p - inOff + nbytes
  }
  if (t0 === 0 && t1 === 0) {
    p += dense
      ? bitunpack128v16(input, p, output, outOff, n, b)
      : bitunpackScalar(input, p, output, outOff, n, b)
    return p - inOff
  }
  if (t0 === 1 && t1 === 0) {
    // bitmap exceptions (b<=16 so exc<<b fits & is masked to 16 bits)
    const bx = input[p]
    p++
    const mapBytes = (n + 7) >> 3
    const exmapOff = p
    // popcount over bitmap bytes
    let nex = 0
    for (let k = 0; k < mapBytes; k++) nex += _POPC[input[exmapOff + k]]
    p += mapBytes
    const exceptions = _exc
    p += bitunpackScalar(input, p, exceptions, 0, nex, bx)
    p += dense
      ? bitunpack128v16(input, p, output, outOff, n, b)
      : bitunpackScalar(input, p, output, outOff, n, b)
    // reinsert byte-wise; skip zero bytes (most bitmap bytes are sparse)
    let e = 0
    for (let k = 0; k < mapBytes; k++) {
      let bm = input[exmapOff + k]
      if (bm === 0) continue
      const base = outOff + (k << 3)
      while (bm) {
        const bit = bm & -bm // lowest set bit
        const r = _POPC[bit - 1] // index of that bit within the byte (popcount of bits below)
        output[base + r] = (output[base + r] + ((exceptions[e] << b) >>> 0)) & 0xffff
        e++
        bm ^= bit
      }
    }
    return p - inOff
  }
  // varbyte exceptions
  const nex = input[p]
  p++
  p += dense
    ? bitunpack128v16(input, p, output, outOff, n, b)
    : bitunpackScalar(input, p, output, outOff, n, b)
  const exceptions = _exc
  p += vbdec16(input, p, exceptions, 0, nex)
  const posOff = p
  for (let i = 0; i < nex; i++) {
    const pos = input[posOff + i]
    output[outOff + pos] = (output[outOff + pos] | ((exceptions[i] << b) >>> 0)) & 0xffff
  }
  p += nex
  return p - inOff
}

export interface P4DecodeResult {
  values: Uint16Array
  read: number
}

export function p4nzdec128v16(input: Uint8Array, inOff: number, count: number): P4DecodeResult {
  const out = new Uint16Array(count)
  let p = inOff
  let o = 0
  while (count - o >= 128) {
    p += p4dec128v16(input, p, out, o, 128, true)
    o += 128
  }
  if (o < count) {
    p += p4dec128v16(input, p, out, o, count - o, false)
    o = count
  }
  return { values: out, read: p - inOff }
}

// ---------------------------------------------------------------------------
// p4nddec64 — 64-bit TurboPFor DELTA decoder (scalar path), used to decode the
// Open-Meteo chunk-index LUT. Faithful clean-room port of the C reference
// (om-file-format: c/src/vp4d.c `P4DEC`/`_p4ddec`, c/src/bitunpack.c
// `bitdunpack64`, c/include/vint.h `_vbxget64` + `vbdec64`).
//
// VERIFIED BIT-EXACT against the @openmeteo WASM oracle for HRRR, MET Nordic,
// UKMO, ICON Global cloud_cover + MET Nordic temperature_2m (see
// _p4nddec64_test.mjs). Replaces the earlier (incorrect) split decode of
// "marker-varint first offset + p4dec128v16 deltas", which only happened to
// work for some files because the even-stride block spacing masked the bug.
//
// A LUT block stores up to 64 ABSOLUTE chunk offsets: a leading varbyte start
// value (vbxget64) followed by a P4DEC delta block. Offsets are < 2^53 for any
// real .om file, so plain `number` arithmetic is exact.
//
// All values here are non-negative offsets/deltas; widths ≤ 64. For widths > 30
// we accumulate bits with multiplication (number) to stay exact past 2^31.
// ---------------------------------------------------------------------------

const POPC8 = new Uint8Array(256)
for (let i = 0; i < 256; i++)
  POPC8[i] =
    (i & 1) + ((i >> 1) & 1) + ((i >> 2) & 1) + ((i >> 3) & 1) +
    ((i >> 4) & 1) + ((i >> 5) & 1) + ((i >> 6) & 1) + ((i >> 7) & 1)

const u16le = (b: Uint8Array, p: number): number => b[p] | (b[p + 1] << 8)
const u32le = (b: Uint8Array, p: number): number =>
  (b[p] | (b[p + 1] << 8) | (b[p + 2] << 16) | (b[p + 3] << 24)) >>> 0
const bswap16x = (x: number): number => ((x & 0xff) << 8) | ((x >> 8) & 0xff)
const bswap32x = (x: number): number =>
  (((x & 0xff) << 24) | ((x & 0xff00) << 8) | ((x >> 8) & 0xff00) | ((x >> 24) & 0xff)) >>> 0

interface U64Varint {
  value: number
  len: number
}

// Leading absolute start value of a LUT block (`_vbxget64`).
function vbxget64(b: Uint8Array, p: number): U64Varint {
  const x0 = b[p]
  if (!(x0 & 0x80)) return { value: x0, len: 1 }
  if (!(x0 & 0x40)) return { value: bswap16x(u16le(b, p) & 0xff3f), len: 2 }
  if (!(x0 & 0x20)) return { value: (((x0 & 0x1f) << 16) | u16le(b, p + 1)) >>> 0, len: 3 }
  if (!(x0 & 0x10)) return { value: bswap32x(u32le(b, p) & 0xffffff0f), len: 4 }
  if (!(x0 & 0x08)) return { value: (x0 & 0x07) * 2 ** 32 + u32le(b, p + 1), len: 5 }
  if (!(x0 & 0x04)) return { value: (bswap16x(u16le(b, p)) & 0x7ff) * 2 ** 32 + u32le(b, p + 2), len: 6 }
  if (!(x0 & 0x02)) return { value: (x0 & 0x03) * 2 ** 48 + u16le(b, p + 1) * 2 ** 32 + u32le(b, p + 3), len: 7 }
  if (!(x0 & 0x01)) {
    // bswap64(u64le) & 0x01ffffffffffffff — value fits in 57 bits (< 2^53? no:
    // up to 2^57). Build big-endian directly from the 8 bytes after the lead.
    let v = 0
    for (let k = 0; k < 7; k++) v = v * 256 + b[p + 1 + k] // 7 high bytes, BE
    v = v * 256 + b[p + 8]
    return { value: v % 2 ** 57, len: 8 }
  }
  // full 8-byte little-endian
  return { value: u32le(b, p + 1) + u32le(b, p + 5) * 2 ** 32, len: 9 }
}

// Generic LSB-first bit unpack into `out` (length n) of nbits each. Returns
// bytes consumed. nbits 0..64; widths > 30 use multiply-accumulate for exactness.
function bitunpack64(b: Uint8Array, p: number, out: number[], n: number, nbits: number): number {
  if (nbits === 0) {
    for (let i = 0; i < n; i++) out[i] = 0
    return 0
  }
  let bitpos = 0
  for (let i = 0; i < n; i++) {
    let v = 0
    let scale = 1
    let got = 0
    while (got < nbits) {
      const byteIdx = p + (bitpos >> 3)
      const bitInByte = bitpos & 7
      const take = Math.min(8 - bitInByte, nbits - got)
      const bits = (b[byteIdx] >> bitInByte) & ((1 << take) - 1)
      v += bits * scale
      scale *= 2 ** take
      got += take
      bitpos += take
    }
    out[i] = v
  }
  return (bitpos + 7) >> 3
}

const VB_OFS1 = 177
const VB_BA2 = 241
const VB_BA3 = 249
const VB_OFS2 = 16561

// Decode n varbyte 64-bit exception values into `out`. Returns bytes consumed.
function vbdec64(b: Uint8Array, p: number, out: number[], n: number): number {
  let q = p
  for (let i = 0; i < n; i++) {
    const x0 = b[q++]
    if (x0 < VB_OFS1) out[i] = x0
    else if (x0 < VB_BA2) {
      out[i] = (x0 << 8) + b[q] + (VB_OFS1 - (VB_OFS1 << 8))
      q++
    } else if (x0 < VB_BA3) {
      out[i] = u16le(b, q) + ((x0 - VB_BA2) << 16) + VB_OFS2
      q += 2
    } else {
      const nb = x0 - VB_BA3
      const total = 3 + nb
      let acc = 0
      let scale = 1
      for (let k = 0; k < total; k++) {
        acc += b[q + k] * scale
        scale *= 256
      }
      out[i] = acc
      q += total
    }
  }
  return q - p
}

const _ex64: number[] = []
const _base64: number[] = []

// P4DEC delta core: decode n delta-coded values starting at byte p, accumulating
// from `start`, writing absolute values into out[outOff..outOff+n). Returns
// bytes consumed.
function p4ddec64(
  b: Uint8Array,
  p: number,
  out: number[],
  outOff: number,
  n: number,
  start: number,
): number {
  let q = p
  const hdr = b[q++]

  if ((hdr & 0xc0) === 0xc0) {
    // constant block: single delta `u` repeated.
    let bb = hdr & 0x3f
    if (bb === 63) bb = 64
    const nbytes = (bb + 7) >> 3
    let u = 0
    let scale = 1
    for (let k = 0; k < nbytes; k++) {
      u += b[q + k] * scale
      scale *= 256
    }
    if (bb < 53) u = u % 2 ** bb
    q += nbytes
    let acc = start
    for (let i = 0; i < n; i++) {
      acc += u
      out[outOff + i] = acc
    }
    return q - p
  }

  if (!(hdr & 0x40)) {
    // PFOR (optionally with bitmap exceptions when hdr & 0x80).
    let bb = hdr & 0x7f
    let bx = 0
    const hasExc = !!(hdr & 0x80)
    if (hasExc) {
      bx = b[q++]
      bb &= 0x7f
    }
    if (bb === 63) bb = 64
    const base = _base64
    if (base.length < n) base.length = n
    let bitmapOff = -1
    let mapBytes = 0
    let nex = 0
    if (hasExc) {
      mapBytes = (n + 7) >> 3
      bitmapOff = q
      for (let k = 0; k < mapBytes; k++) nex += POPC8[b[bitmapOff + k]]
      q += mapBytes
      const ex = _ex64
      if (ex.length < nex) ex.length = nex
      q += bitunpack64(b, q, ex, nex, bx)
      q += bitunpack64(b, q, base, n, bb)
      // reinsert exceptions at set-bit positions.
      let e = 0
      const pow2bb = 2 ** bb
      for (let k = 0; k < mapBytes; k++) {
        let bm = b[bitmapOff + k]
        if (bm === 0) continue
        const baseIdx = k << 3
        while (bm) {
          const bit = bm & -bm
          const r = POPC8[bit - 1]
          base[baseIdx + r] += ex[e] * pow2bb
          e++
          bm ^= bit
        }
      }
    } else {
      q += bitunpack64(b, q, base, n, bb)
    }
    let acc = start
    for (let i = 0; i < n; i++) {
      acc += base[i]
      out[outOff + i] = acc
    }
    return q - p
  }

  // variable-byte exceptions.
  let bb = hdr & 0x3f
  const bx = b[q++]
  if (bb === 63) bb = 64
  const base = _base64
  if (base.length < n) base.length = n
  q += bitunpack64(b, q, base, n, bb)
  const ex = _ex64
  if (ex.length < bx) ex.length = bx
  q += vbdec64(b, q, ex, bx)
  const pow2bb = 2 ** bb
  for (let i = 0; i < bx; i++) {
    const pos = b[q + i]
    // OR with shifted exception (positions are distinct so += is equivalent).
    base[pos] += ex[i] * pow2bb
  }
  q += bx
  let acc = start
  for (let i = 0; i < n; i++) {
    acc += base[i]
    out[outOff + i] = acc
  }
  return q - p
}

// Decode one LUT block (up to 64 absolute offsets) at byte offset `p` into
// out[outOff..outOff+n). Leading vbxget64 start + P4DEC delta block.
export function p4nddec64(b: Uint8Array, p: number, out: number[], outOff: number, n: number): void {
  const s = vbxget64(b, p)
  out[outOff] = s.value
  if (n > 1) p4ddec64(b, p + s.len, out, outOff + 1, n - 1, s.value)
}
